import { getEGenderList, getELanguageList } from '@if/core/types'
import { ControlledDateOnlyPicker, ControlledSelect, ControlledTextField, useTranslatedOptionEnum } from '@if/mui'
import { Button, Grid, Stack, Typography } from '@mui/material'
import { useDevTools } from '@src/types/constants'
import { useState } from 'react'
import { Control, FieldValues, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import BorrowerAddressInputRow from './BorrowerAddressInputRow'
import ListCreditTestCaseDialog from './ListCreditTestCaseDialog'
import { CustomerSchemaType, NewLoanDtoSchemaType } from './loan-form-schema'

interface Props<TFieldValues extends FieldValues> {
  control: Control<NewLoanDtoSchemaType, TFieldValues>
  isBorrower: boolean
  setValue: UseFormSetValue<NewLoanDtoSchemaType>
}

const BorrowerInputRow = <TFieldValues extends FieldValues = FieldValues>({
  control,
  isBorrower,
  setValue,
}: Props<TFieldValues>) => {
  const { t } = useTranslation()
  const genderOptions = useTranslatedOptionEnum('EGender', getEGenderList(), 'enum')
  const languageOptions = useTranslatedOptionEnum('ELanguage', getELanguageList(), 'enum')
  const suffix = isBorrower ? 'borrower' : 'coBorrower'
  const title = t(`loan.borrower.${suffix}`)
  const [openCreditTestCaseDialog, setOpenCreditTestCaseDialog] = useState<boolean>(false)

  const onTestSelected = (applicant: CustomerSchemaType) => {
    setValue(suffix, applicant, { shouldValidate: true })
    setOpenCreditTestCaseDialog(false)
  }

  return (
    <Grid size={12}>
      <Grid container spacing={2}>
        <Grid size={12}>
          {useDevTools && (
            <Button
              onClick={() => setOpenCreditTestCaseDialog(true)}
              type="button"
              variant="contained"
              color="secondary"
              sx={{ marginBottom: 1 }}
            >
              test case
            </Button>
          )}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{title}</Typography>
          </Stack>
        </Grid>
        <Grid size={4}>
          <ControlledTextField control={control} name={`${suffix}.firstName`} label={t('loan.borrower.firstName')} />
        </Grid>
        <Grid size={4}>
          <ControlledTextField control={control} name={`${suffix}.middleName`} label={t('loan.borrower.middleName')} />
        </Grid>
        <Grid size={4}>
          <ControlledTextField control={control} name={`${suffix}.lastName`} label={t('loan.borrower.lastName')} />
        </Grid>
        <Grid size={4}>
          <ControlledDateOnlyPicker
            control={control}
            name={`${suffix}.birthDate`}
            label={t('loan.borrower.birthdate')}
          />
        </Grid>
        <Grid size={4}>
          <ControlledSelect
            control={control}
            name={`${suffix}.languageCode`}
            label={t('loan.borrower.language')}
            options={languageOptions}
          />
        </Grid>
        <Grid size={4}>
          <ControlledSelect
            control={control}
            name={`${suffix}.genderId`}
            label={t('loan.borrower.gender')}
            options={genderOptions}
          />
        </Grid>
        <Grid size={4}>
          <ControlledTextField
            control={control}
            type="number"
            name={`${suffix}.alisId`}
            label={t('loan.borrower.alisId')}
          />
        </Grid>
        <Grid size={4}>
          <ControlledTextField
            control={control}
            type="number"
            name={`${suffix}.monolithId`}
            label={t('loan.borrower.monolithId')}
          />
        </Grid>
        <Grid size={4}>
          <ControlledTextField
            control={control}
            name={`${suffix}.equifaxNumber`}
            label={t('loan.borrower.equifaxNumber')}
          />
        </Grid>
        <BorrowerAddressInputRow control={control} isBorrower={isBorrower} />
      </Grid>
      <ListCreditTestCaseDialog
        onClose={() => setOpenCreditTestCaseDialog(false)}
        open={openCreditTestCaseDialog}
        onTestCaseSelected={onTestSelected}
      />
    </Grid>
  )
}

export default BorrowerInputRow
